import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserDTO } from '@va/shared';
import { backend, type LoginResult } from '../api/client';

interface AuthState {
  user: UserDTO | null;
  ready: boolean;
  login(email: string, password: string): Promise<LoginResult>;
  loginMfa(ticket: string, code: string): Promise<UserDTO>;
  register(input: { email: string; password: string; displayName?: string; acceptTerms: boolean }): Promise<UserDTO>;
  logout(): Promise<void>;
  setUser(user: UserDTO): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUserState] = useState<UserDTO | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    backend
      .restore()
      .then((u) => alive && setUserState(u))
      .catch(() => alive && setUserState(null))
      .finally(() => alive && setReady(true));
    const off = backend.subscribe((u) => {
      setUserState((prev) => {
        // Account switched or signed out: everything account-specific is stale.
        if (prev?.id !== u?.id) qc.invalidateQueries();
        return u;
      });
    });
    return () => {
      alive = false;
      off();
    };
  }, [qc]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await backend.login(email, password);
    if (r.ok) setUserState(r.user);
    return r;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      ready,
      login,
      loginMfa: async (ticket, code) => {
        const u = await backend.loginMfa(ticket, code);
        setUserState(u);
        return u;
      },
      register: async (input) => {
        const u = await backend.register(input);
        setUserState(u);
        return u;
      },
      logout: async () => {
        await backend.logout();
        setUserState(null);
        qc.clear();
      },
      setUser: (u) => setUserState(u),
    }),
    [user, ready, login, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
